import React, { useState } from 'react';
import { Check, Clock, Users, Flame, ExternalLink, ChevronDown, ChevronUp, ShoppingBasket, ListOrdered, Lightbulb } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const NutritionRing = ({ label, value, unit, color, bgColor, max = 100 }) => {
    const pct = Math.min((value || 0) / max, 1);
    const radius = 26;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - pct);

    return (
        <div className="flex flex-col items-center gap-1">
            <div className="relative w-16 h-16">
                <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
                    <circle cx="32" cy="32" r={radius} fill="none" stroke="currentColor"
                        className="text-secondary" strokeWidth="5" />
                    <circle cx="32" cy="32" r={radius} fill="none" stroke={color}
                        strokeWidth="5" strokeLinecap="round"
                        strokeDasharray={circumference} strokeDashoffset={offset}
                        style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-sm font-bold leading-none">{value || '-'}</span>
                    <span className="text-[9px] text-muted-foreground">{unit}</span>
                </div>
            </div>
            <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
        </div>
    );
};

const MetaPill = ({ icon: Icon, children }) => (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/60 text-sm text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />
        <span>{children}</span>
    </div>
);

const RecipeView = ({ recipe, url }) => {
    const [checkedIngredients, setCheckedIngredients] = useState({});
    const [showTips, setShowTips] = useState(false);

    if (!recipe) return null;

    const toggleIngredient = (idx) => {
        setCheckedIngredients(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const checkedCount = Object.values(checkedIngredients).filter(Boolean).length;
    const totalIngredients = recipe.ingredients?.length || 0;

    const isHebrew = (text) => {
        if (!text) return false;
        const hebrewChars = (text.match(/[\u0590-\u05FF]/g) || []).length;
        const latinChars = (text.match(/[a-zA-Z]/g) || []).length;
        return hebrewChars > latinChars;
    };

    const sampleText = [
        recipe.recipe_title,
        recipe.ingredients?.[0]?.name,
        recipe.instructions?.[0]
    ].filter(Boolean).join(' ');
    const recipeIsRtl = isHebrew(sampleText);
    const dir = recipeIsRtl ? 'rtl' : 'ltr';

    const nutrition = recipe.nutrition || {};
    const hasNutrition = nutrition.calories > 0 || nutrition.protein > 0;

    return (
        <div className="space-y-8" dir={dir} data-testid="recipe-view">

            {/* Meta row - time / servings */}
            <div className="flex flex-wrap gap-2">
                {recipe.prep_time && <MetaPill icon={Clock}>Prep: {recipe.prep_time}</MetaPill>}
                {recipe.cook_time && <MetaPill icon={Flame}>Cook: {recipe.cook_time}</MetaPill>}
                {recipe.servings && <MetaPill icon={Users}>{recipe.servings}</MetaPill>}
            </div>

            {/* Nutrition rings */}
            {hasNutrition && (
                <div data-testid="nutrition-bar"
                    className="p-5 rounded-2xl bg-gradient-to-br from-secondary/40 to-secondary/20 border border-border/40">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Nutrition per serving</p>
                    <div className="flex justify-around">
                        <NutritionRing label="Calories" value={nutrition.calories} unit="kcal" color="hsl(24, 90%, 55%)" max={800} />
                        <NutritionRing label="Protein" value={nutrition.protein} unit="g" color="hsl(0, 70%, 55%)" max={60} />
                        <NutritionRing label="Fat" value={nutrition.fat} unit="g" color="hsl(45, 85%, 50%)" max={60} />
                        <NutritionRing label="Carbs" value={nutrition.carbs} unit="g" color="hsl(215, 70%, 55%)" max={100} />
                        {nutrition.fiber > 0 && (
                            <NutritionRing label="Fiber" value={nutrition.fiber} unit="g" color="hsl(152, 60%, 40%)" max={30} />
                        )}
                    </div>
                </div>
            )}

            {/* Watch original */}
            {url && (
                <a href={url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                    data-testid="play-video-btn">
                    <ExternalLink className="w-4 h-4" />
                    Watch Original Video
                </a>
            )}

            {/* ─── Ingredients ─── */}
            {recipe.ingredients && recipe.ingredients.length > 0 && (
                <div data-testid="ingredients-section">
                    {/* Header with progress */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                                <ShoppingBasket className="w-4 h-4 text-emerald-700" />
                            </div>
                            <h3 className="text-base font-semibold font-heading">Ingredients</h3>
                        </div>
                        {totalIngredients > 0 && (
                            <span className="text-xs font-medium text-muted-foreground tabular-nums">
                                {checkedCount}/{totalIngredients}
                            </span>
                        )}
                    </div>

                    {/* Progress bar */}
                    {totalIngredients > 0 && (
                        <div className="h-1 rounded-full bg-secondary mb-4 overflow-hidden">
                            <motion.div
                                className="h-full rounded-full bg-emerald-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${(checkedCount / totalIngredients) * 100}%` }}
                                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            />
                        </div>
                    )}

                    {/* Ingredient list */}
                    <div className="rounded-2xl border border-border/50 overflow-hidden divide-y divide-border/40">
                        {recipe.ingredients.map((ing, idx) => {
                            const isChecked = checkedIngredients[idx];
                            return (
                                <button
                                    key={idx}
                                    onClick={() => toggleIngredient(idx)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 ${
                                        isChecked
                                            ? 'bg-emerald-50/60'
                                            : 'bg-white hover:bg-secondary/30'
                                    }`}
                                    data-testid={`ingredient-${idx}`}
                                >
                                    {/* Custom checkbox */}
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${
                                        isChecked
                                            ? 'bg-emerald-500 border-emerald-500 scale-110'
                                            : 'border-border/80 hover:border-emerald-400'
                                    }`}>
                                        <AnimatePresence>
                                            {isChecked && (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    exit={{ scale: 0 }}
                                                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                                                >
                                                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Amount + name */}
                                    <span className={`flex-1 text-start transition-all duration-200 ${
                                        isChecked ? 'line-through text-muted-foreground/60' : ''
                                    }`}>
                                        {ing.amount && (
                                            <span className="font-semibold text-foreground/90">{ing.amount}{' '}</span>
                                        )}
                                        <span className={isChecked ? '' : 'text-foreground/80'}>{ing.name}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ─── Instructions ─── */}
            {recipe.instructions && recipe.instructions.length > 0 && (
                <div data-testid="instructions-section">
                    <div className="flex items-center gap-2.5 mb-5">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                            <ListOrdered className="w-4 h-4 text-amber-700" />
                        </div>
                        <h3 className="text-base font-semibold font-heading">Instructions</h3>
                    </div>

                    {/* Timeline */}
                    <div className="relative">
                        {/* Vertical connector */}
                        <div className={`absolute ${recipeIsRtl ? 'right-[19px]' : 'left-[19px]'} top-5 bottom-5 w-px bg-border/60`} />

                        <div className="space-y-0">
                            {recipe.instructions.map((step, idx) => (
                                <div key={idx} className="relative flex gap-4 group" data-testid={`step-${idx}`}>
                                    {/* Step number */}
                                    <div className="relative z-10 shrink-0 w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shadow-sm mt-1">
                                        {idx + 1}
                                    </div>

                                    {/* Step content */}
                                    <div className={`flex-1 pb-6 ${idx === recipe.instructions.length - 1 ? 'pb-0' : ''}`}>
                                        <div className="p-4 rounded-2xl bg-secondary/30 border border-border/30 group-hover:bg-secondary/50 transition-colors duration-200">
                                            <p className="text-sm leading-relaxed text-foreground/85">{step}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Source */}
            {recipe.source && (
                <div className="text-sm text-muted-foreground">
                    Source: <span className="font-medium text-foreground">{recipe.source}</span>
                </div>
            )}

            {/* ─── Tips ─── */}
            {recipe.tips && (
                <div data-testid="tips-section">
                    <button
                        onClick={() => setShowTips(!showTips)}
                        className="flex items-center gap-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                            <Lightbulb className="w-3.5 h-3.5 text-amber-600" />
                        </div>
                        Tips & Notes
                        {showTips ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <AnimatePresence>
                        {showTips && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                            >
                                <div className="mt-3 p-4 rounded-2xl bg-amber-50/70 border border-amber-200/50 text-amber-900 text-sm leading-relaxed">
                                    {recipe.tips}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
};

export default RecipeView;
